import React from 'react';
import styled from 'styled-components';
import { Layout, Menu, theme } from 'antd';
import { useCookies } from 'react-cookie';
import { NavLink } from 'react-router-dom';
import { AdminMenuList, MenuListName } from 'utils/constant';
import SidebarLogo from './SidebarLogo';
import SidebarBottom from './SidebarBottom';
import { theme as globalTheme } from 'utils/style/theme';
import { UserRole } from 'utils/types/common';
import { useAppSelector } from 'hooks/redux';

const { Sider } = Layout;

interface IProps {
  collapsed: boolean;
  onCollapse: (e: any) => void;
  onSelect: (e: any) => void;
  onOpenChange: (e: any) => void;
  selectMenu: string;
  handleCollapse: (e: React.MouseEvent) => void;
}

const Sidebar = ({
  collapsed = false,
  onCollapse,
  onSelect,
  onOpenChange,
  selectMenu,
  handleCollapse,
}: IProps) => {
  const [cookies] = useCookies();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const menuList = MenuListName();
  const adminMenu = AdminMenuList();

  const dataSource: string = cookies?.dataSource || 'dev';
  const loginUser = useAppSelector((state) => state.userSlice.userInfor);

  return (
    <ASider
      $dataSource={dataSource as 'live' | 'dev'}
      width={268}
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        background: globalTheme.colors.blue_500,
      }}
    >
      <ASideContentWrapper>
        <SidebarLogo collapsed={collapsed} handleCollapse={handleCollapse} />
        <MenuWrapper collapsed={collapsed}>
          <Menu
            mode='inline'
            selectedKeys={[selectMenu]}
            onSelect={onSelect}
            openKeys={cookies?.openMenu}
            onOpenChange={onOpenChange}
          >
            {/* 일반 메뉴 구성 */}
            {menuList.map((menu) =>
              !menu.subMenus ? (
                <Menu.Item
                  key={menu.key}
                  icon={
                    selectMenu === menu.key ? (
                      <IconMenuCollapse>
                        {menu.iconOn}
                        {collapsed && menu.nameWhenCollapse}
                      </IconMenuCollapse>
                    ) : (
                      <IconMenuCollapse>
                        {menu.icon}
                        {collapsed && menu.nameWhenCollapse}
                      </IconMenuCollapse>
                    )
                  }
                >
                  <NavLink to={`${menu.path}`}>{menu.name}</NavLink>
                </Menu.Item>
              ) : (
                <Menu.SubMenu
                  key={menu.key}
                  title={menu.name}
                  icon={
                    <IconMenuCollapse>
                      {menu.key === selectMenu ? menu.iconOn : menu.icon}
                      {collapsed && menu.nameWhenCollapse}
                    </IconMenuCollapse>
                  }
                >
                  {menu.subMenus.map((sub) => (
                    <Menu.Item
                      key={sub.key}
                      icon={selectMenu === sub.key ? sub.iconOn : sub.icon}
                    >
                      <NavLink to={`${sub.path}`}>{sub.name}</NavLink>
                    </Menu.Item>
                  ))}
                </Menu.SubMenu>
              ),
            )}

            {/* 관리자 메뉴 구성 */}
            {loginUser?.role == UserRole.SuperAdmin &&
              adminMenu.map((menu) =>
                !menu.subMenus ? (
                  <Menu.Item
                    key={menu.key}
                    icon={
                      selectMenu === menu.key ? (
                        <IconMenuCollapse>
                          {menu.iconOn}
                          {collapsed && menu.nameWhenCollapse}
                        </IconMenuCollapse>
                      ) : (
                        <IconMenuCollapse>
                          {menu.icon}
                          {collapsed && menu.nameWhenCollapse}
                        </IconMenuCollapse>
                      )
                    }
                  >
                    <NavLink to={`${menu.path}`}>{menu.name}</NavLink>
                  </Menu.Item>
                ) : (
                  <Menu.SubMenu
                    key={menu.key}
                    title={menu.name}
                    icon={
                      <IconMenuCollapse>
                        {menu.key === selectMenu ? menu.iconOn : menu.icon}
                        {collapsed && menu.nameWhenCollapse}
                      </IconMenuCollapse>
                    }
                  >
                    {menu.subMenus.map((sub) => (
                      <Menu.Item
                        key={sub.key}
                        icon={selectMenu === sub.key ? sub.iconOn : sub.icon}
                      >
                        <NavLink to={`${sub.path}`}>{sub.name}</NavLink>
                      </Menu.Item>
                    ))}
                  </Menu.SubMenu>
                ),
              )}
          </Menu>
        </MenuWrapper>

        {!collapsed && (
          <>
            <SidebarBottom />{' '}
          </>
        )}
      </ASideContentWrapper>
    </ASider>
  );
};

export default Sidebar;

const ASider = styled(Sider)<{ $dataSource: 'live' | 'dev' }>`
  .ant-menu-sub > .ant-menu-item {
    padding-left: 32px !important;
    margin-block: 0 !important;
  }
  .ant-menu-sub > li > .ant-menu-title-content {
    border-left: 1px solid #7496e2;
    position: relative;
  }
  .ant-menu-sub > li:last-child > .ant-menu-title-content {
    border: none;
  }
  .ant-menu-sub > li:last-child > .ant-menu-title-content::before {
    content: '';
    display: block;
    height: 36%;
    width: 1px;
    background-color: #7496e2;
    position: absolute;
    top: 0;
    left: 0;
  }

  .ant-menu-sub > li > .ant-menu-title-content > a {
    margin-left: 25px;
  }
  .ant-menu-sub > li > .ant-menu-title-content::after {
    content: '';
    display: block;
    height: 100%;
    width: 20px;
    background-color: transparent;
    background-image: url('/images/sidebar-line.svg');
    background-repeat: no-repeat;
    position: absolute;
    left: -2px;
    top: 79%;
    transform: translateY(-50%);
  }

  .ant-menu-sub > li:last-child > .ant-menu-title-content::after {
    left: -1px;
  }

  .ant-layout-sider-children,
  .ant-menu {
    background: ${(props) =>
      props.$dataSource === 'live'
        ? globalTheme.colors.red_500
        : globalTheme.colors.blue_500} !important;
  }

  .ant-menu-title-content {
    color: #ffffff;
    padding-left: ${(props) => (props.collapsed ? '0' : '24px')};
  }
  .ant-menu-light.ant-menu-root.ant-menu-inline {
    border-inline-end: unset;
  }
  img {
    width: 1.25rem;
    /* margin-top: -0.09rem; */
  }
  position: relative;
  background: ${globalTheme.colors.blue_500} !important;
`;

const ASideContentWrapper = styled.div`
  position: relative;
  min-height: 92vh;
`;

const MenuWrapper = styled.div<{ collapsed: boolean }>`
  margin-top: 2.063rem;
  .ant-menu-item.ant-menu-item-selected {
    background-color: ${globalTheme.colors.blue_700};
  }
  .ant-menu-submenu-arrow {
    color: #ffffff;
  }
  .ant-menu-item-icon {
    line-height: normal !important;
    position: absolute;
    top: 50%;
    left: ${(props) => (props.collapsed ? '50%' : '8%')};
    transform: translate(-50%, -50%);
  }
  .ant-menu-submenu-title,
  .ant-menu-item {
    position: relative;
    height: ${(props) => (props.collapsed ? '50px' : '40px')};
    /* padding-left: calc(50% - 20px); */
    /* margin-block: ${(props) => (props.collapsed ? '12px' : '8px')}; */
  }
  .ant-menu-submenu-title {
    display: flex;
  }
  .ant-menu-submenu {
    padding-bottom: 0px !important;
    /* padding-left : ${(props) => (props.collapsed ? '0' : '30px')}; */
  }

  .ant-menu {
    & > * + * {
      margin-top: ${(props) => (props.collapsed ? '24px' : '8px')};
    }
  }
`;

const IconMenuCollapse = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px !important;
  font-weight: 400 !important;
  line-height: 20px !important;
  letter-spacing: 0.2px !important;
  color: white !important;
`;
